import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Alert,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { requestClienteByDoc } from "../../actions/cliente";
import { changePasswordEmail } from "../../actions/auth";
import rutas from "../../routes/constants/routes";
import TokenService from "../../services/token";
import React from "react";
import {
  Cliente,
  FormErrors,
  FormTouched,
  FormValues,
  Props,
  StateProps,
} from "../../interface/changePassword";

const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const passwordRules = {
  minLength: 6,
  hasUppercase: /[A-Z]/,
  hasSpecialChar: /[^A-Za-z0-9]/,
};

const ChangePassword = (props: Props) => {
  const { nroDoc, tipoDoc, email } =
    useParams<{
      nroDoc: string;
      tipoDoc: string;
      email: string;
    }>();

  const captchaTokenLS = localStorage.getItem("_grecaptcha");
  const history = useHistory();
  const hasSession = TokenService.hasSession();

  const [values, setValues] = useState<FormValues>({
    identification: nroDoc ?? "",
    identificationType: tipoDoc ?? "",
    email: email ?? "",
    passwordCopy: "",
    newPassword: "",
    password: "",
    captchaToken: captchaTokenLS,
    ChangePassword: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});

  useEffect(() => {
    if (props.successPasswordEmail) {
      history.push(rutas.LOGIN);
      window.location.reload();
    }
  }, [props.successPasswordEmail]);

  useEffect(() => {
    if (nroDoc && tipoDoc) {
      props.requestClienteByDoc(nroDoc, tipoDoc);
    }
  }, []);

  const evaluatePassword = (password: string) => {
    const checks = {
      minLength: password.length >= passwordRules.minLength,
      hasUppercase: passwordRules.hasUppercase.test(password),
      hasSpecialChar: passwordRules.hasSpecialChar.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;

    return { checks, score };
  };

  const passwordEvaluation = React.useMemo(
    () => evaluatePassword(values.newPassword),
    [values.newPassword],
  );

  const getStrengthColor = (score: number) => {
    switch (score) {
      case 1:
        return "error";
      case 2:
        return "warning";
      case 3:
        return "success";
      default:
        return "error";
    }
  };

  const validate = (values: FormValues, hasSession: boolean): FormErrors => {
    const errors: FormErrors = {};

    if (hasSession && !values.password) {
      errors.password = "Ingrese su contraseña actual";
    }

    if (!values.passwordCopy) {
      errors.passwordCopy = "Ingrese una contraseña";
    } else if (!PASSWORD_REGEX.test(values.passwordCopy)) {
      errors.passwordCopy =
        "Debe tener: 1 mayúscula, 1 número, 1 caracter especial y una longitud mínima de 8";
    }

    if (!values.newPassword) {
      errors.newPassword = "Ingrese nuevamente la contraseña";
    } else if (values.newPassword !== values.passwordCopy) {
      errors.newPassword = "Las contraseñas deben coincidir";
    }

    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updated = { ...values, [name]: value };
    setValues(updated);

    // Revalidate touched fields on change
    if (touched[name as keyof FormTouched]) {
      setErrors(validate(updated, hasSession));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(values, hasSession));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ password: true, passwordCopy: true, newPassword: true });
    const validationErrors = validate(values, hasSession);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      props.changePasswordEmail(values);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        px: 2,
        my: 4,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "30vw" }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: "#e2e2e2",
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: "flex",
            gap: 3,
          }}
        >
          <Box>
            {["N° Cliente", "CUIT", "Empresa"].map((label) => (
              <Typography key={label} variant="h6" fontWeight={700}>
                {label}
              </Typography>
            ))}
          </Box>
          <Box>
            <Typography variant="h6" color="text.secondary" fontWeight={300}>
              {props.cliente?.id ?? "—"}
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={300}>
              {nroDoc}
            </Typography>
            <Typography variant="h6" color="text.secondary" fontWeight={300}>
              {props.cliente?.razonSocial ?? "—"}
            </Typography>
          </Box>
        </Paper>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {hasSession && (
            <TextField
              fullWidth
              margin="normal"
              id="password"
              name="password"
              label="Contraseña actual"
              type="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              sx={{ "& .MuiOutlinedInput-root": { borderColor: "black" } }}
            />
          )}

          <TextField
            fullWidth
            margin="normal"
            id="passwordCopy"
            name="passwordCopy"
            label="Nueva contraseña"
            type="password"
            value={values.passwordCopy}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.passwordCopy && Boolean(errors.passwordCopy)}
            helperText={
              touched.passwordCopy && errors.passwordCopy
                ? errors.passwordCopy
                : "Debe tener: 1 mayúscula, 1 número, 1 caracter especial y una longitud mínima de 8."
            }
          />

          <TextField
            fullWidth
            margin="normal"
            id="newPassword"
            name="newPassword"
            label="Repetir contraseña"
            type="password"
            value={values.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.newPassword && Boolean(errors.newPassword)}
            helperText={touched.newPassword && errors.newPassword}
          />

          <LinearProgress
            variant="determinate"
            value={(passwordEvaluation.score / 3) * 100}
            color={getStrengthColor(passwordEvaluation.score)}
            sx={{ mt: 1, height: 6, borderRadius: 3 }}
          />

          <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
            Seguridad de la contraseña
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={Boolean(props.error) || props.loading}
              startIcon={
                props.loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              sx={{
                bgcolor: "#E3000B",
                px: 6,
                py: 1.5,
                "&:hover": { bgcolor: "#b8000a" },
                "&.Mui-disabled": { bgcolor: "#e57373", color: "white" },
              }}
            >
              Confirmar
            </Button>
          </Box>

          {props.errorPasswordEmail && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {props.errorPasswordEmail}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default connect(
  (state: {
    auth: {
      loading: boolean;
      successPasswordEmail: boolean;
      error: StateProps["error"];
      errorPasswordEmail: string;
    };
    clientes: { cliente: Cliente | null };
  }) => ({
    loading: state.auth.loading,
    successPasswordEmail: state.auth.successPasswordEmail,
    cliente: state.clientes.cliente,
    error: state.auth.error,
    errorPasswordEmail: state.auth.errorPasswordEmail,
  }),
  (dispatch) => ({
    requestClienteByDoc: (nroDoc: string, tipoDoc: string) =>
      dispatch(requestClienteByDoc(nroDoc, tipoDoc)),
    changePasswordEmail: (data: FormValues) =>
      dispatch(changePasswordEmail(data)),
  }),
)(ChangePassword);
